import { useEffect } from "react";
import { type LexicalEditor } from "lexical";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $generateHtmlFromNodes } from "@lexical/html";

import { sanitiseHtml } from "../sanitiseHtml";

// Lexical always outputs at least one <p> even when the editor is empty.
// This pattern matches those empty states so we can return "" instead.
const LEXICAL_EMPTY_STATE: RegExp = /^(<p><br><\/p>|<p><\/p>|<p>\s*<\/p>)$/;

const getHtml = (editor: LexicalEditor): string => {
  return editor.getEditorState().read((): string => {
    const rawHtml: string = $generateHtmlFromNodes(editor);
    const sanitised: string = sanitiseHtml(rawHtml);
    return LEXICAL_EMPTY_STATE.test(sanitised.trim()) ? "" : sanitised;
  });
}

interface HtmlOnChangePluginProps {
  onChange?: (value: string) => void;
  onBlur?: (value: string) => void;
}

/**
 * Emits v1-compatible HTML on every change and on blur.
 */
export const HtmlOnChangePlugin = (props: HtmlOnChangePluginProps): null => {
  const [editor] = useLexicalComposerContext();

  // registerUpdateListener fires on every editor state change.
  // dirtyElements/dirtyLeaves are empty when nothing actually changed
  // (e.g. focus moves) — skip those to avoid unnecessary calls.
  useEffect((): (() => void) | void => {
    if (!props.onChange) {
      return;
    }
    return editor.registerUpdateListener(({ dirtyElements, dirtyLeaves }): void => {
      if (dirtyElements.size === 0 && dirtyLeaves.size === 0) {
        return;
      }
      props.onChange!(getHtml(editor));
    });
  }, [editor, props.onChange]);

  useEffect((): (() => void) | void => {
    if (!props.onBlur) {
      return;
    }
    const rootElement: HTMLElement = editor.getRootElement()!;
    const handleBlur = (): void => props.onBlur!(getHtml(editor));
    rootElement.addEventListener("blur", handleBlur);
    return (): void => rootElement.removeEventListener("blur", handleBlur);
  }, [editor, props.onBlur]);

  return null;
};
